import gql from 'graphql-tag'
import isArray from 'lodash/isArray'
import type { DocumentNode, FragmentDefinitionNode, SelectionSetNode, DefinitionNode, FieldNode } from 'graphql'
import { visit, print, Kind } from 'graphql'
import isEmpty from 'lodash/isEmpty'
import first from 'lodash/first'
import find from 'lodash/find'
import camelCase from 'lodash/camelCase'
import { getOperationName } from '@apollo/client/utilities'

export type BuildGraphqlQueryFragment = string | DocumentNode
export type QueryConfig = {
  _type: string
  entity: string
  query: DocumentNode
  queryAsString: string
}
export type MutationConfig = {
  refetchQueries: string[]
} & QueryConfig

export type GraphqlQueryConfig = {
  createConfig: MutationConfig
  updateConfig: MutationConfig
  deleteConfig: MutationConfig
  singleConfig: QueryConfig & { extraOptions: any }
  allConfig: QueryConfig
  allNoMetaConfig: QueryConfig
  countConfig: QueryConfig
  searchConfig: QueryConfig
  updateManyConfig: MutationConfig
  deleteManyConfig: MutationConfig
  subscription: {
    created: Omit<MutationConfig, 'refetchQueries'>
    updated: Omit<MutationConfig, 'refetchQueries'>
    deleted: Omit<MutationConfig, 'refetchQueries'>
  }
} & Record<any, any>

const isDocumentNodeFragment = (documentFragment: BuildGraphqlQueryFragment) => {
  return {
    isDocumentNode: typeof documentFragment !== 'string',
    isArrayDocumentNode: isArray(documentFragment),
  }
}

function getDefaultFragmentName(doc: DocumentNode): string {
  const fragments: FragmentDefinitionNode[] = []

  visit(doc, {
    FragmentDefinition(node) {
      fragments.push(node)
    },
  })

  //return fragments

  //we need to get the first fragment name,
  const firstFragment = first(fragments)
  let defaultFragment = firstFragment
  let defaultFragmentName = defaultFragment.name.value

  //if the name does not include the word default, we need to find who ever has the name with default
  //else just settle for it
  if (!defaultFragmentName.includes('default')) {
    //we need to look for the fragment name
    defaultFragment = find(fragments, (e) => e.name.value.includes('default'))

    if (defaultFragment) {
      defaultFragmentName = defaultFragment.name.value
    } else {
      //if still cannot find fallback to main
      defaultFragmentName = firstFragment.name.value
    }
  }

  return defaultFragmentName
}

export const buildGraphqlFragment = (documentFragment: BuildGraphqlQueryFragment) => {
  let fragmentQuery: BuildGraphqlQueryFragment = ''
  let fields: BuildGraphqlQueryFragment = documentFragment

  //set the default document fragment
  if (isEmpty(documentFragment)) {
    fields = '' //there should always be a default
  }

  const { isDocumentNode } = isDocumentNodeFragment(documentFragment)

  if (isDocumentNode) {
    const docNode = documentFragment as DocumentNode
    //setup the fragment query
    fragmentQuery = print(docNode)

    //change the document fragment to the main fragment
    fields = `...${getDefaultFragmentName(docNode)}`
  }

  return {
    fragmentQuery,
    fields,
  }
}

export const buildGraphqlQuery = (name: string, documentFragment: BuildGraphqlQueryFragment, subDocuments: string[] = []): GraphqlQueryConfig => {
  const configs: GraphqlQueryConfig = {} as any
  const { fields, fragmentQuery } = buildGraphqlFragment(documentFragment)

  ///////////////////////////////////////////////////
  //CREATE
  ///////////////////////////////////////////////////
  const createQuery = `
    ${fragmentQuery}
    mutation create${name}($input: JSON!) {
      create${name}(input: $input) {
      _id
      ${fields}
    }
    }`
  configs.createConfig = {
    _type: 'create',
    entity: `create${name}`,
    query: gql(createQuery),
    queryAsString: createQuery,
    refetchQueries: [`all${name}`],
  }

  ///////////////////////////////////////////////////
  //UPDATE
  ///////////////////////////////////////////////////
  const updateQuery = `
    ${fragmentQuery} 
    mutation update${name}($input: JSON!, $filter:JSON) {
      update${name}(input: $input, filter: $filter) {
      ${fields}
    }
    }`

  configs.updateConfig = {
    _type: 'update',
    entity: `update${name}`,
    query: gql(updateQuery),
    queryAsString: updateQuery,
    refetchQueries: [`all${name}`, `single${name}`],
  }
  ///////////////////////////////////////////////////
  //DELETE
  ///////////////////////////////////////////////////
  const deleteQuery = `
    ${fragmentQuery} 
    mutation delete${name}($filter: JSON!) {
      delete${name}(filter: $filter) {
      _id
      ${fields}
    }
  }`
  configs.deleteConfig = {
    _type: 'delete',
    entity: `delete${name}`,
    query: gql(deleteQuery),
    queryAsString: deleteQuery,
    refetchQueries: [`all${name}`],
  }

  ///////////////////////////////////////////////////
  //GET SINGLE
  ///////////////////////////////////////////////////
  const singleQuery = `
  ${fragmentQuery}
  query single${name}($filter: JSON ) {
      single${name}(filter: $filter) {
      ${fields}
    }
  }
  `
  const extraOptions = {
    pollInterval: 60000,
  }
  configs.singleConfig = {
    query: gql(singleQuery),
    queryAsString: singleQuery,
    entity: `single${name}`,
    extraOptions,
    _type: 'single',
  }

  ///////////////////////////////////////////////////
  //GET ALL
  ///////////////////////////////////////////////////
  const allQuery = `
    ${fragmentQuery}
    query all${name}($filter: JSON!, $options: JSON) {
      all${name}(filter: $filter, options: $options) {
      ${fields}
    }

    all${name}Meta(filter: $filter) {
    count
    }
    }
  `
  configs.allConfig = {
    query: gql(allQuery),
    queryAsString: allQuery,
    entity: `all${name}`,
    _type: 'all',
  }

  ///////////////////////////////////////////////////
  //COUNT DOCUMENTS
  ///////////////////////////////////////////////////
  const countQuery = `   
    query all${name}($filter: JSON!) {     
      all${name}Meta(filter: $filter) {
          count
      }
    }
  `
  configs.countConfig = {
    query: gql(countQuery),
    queryAsString: countQuery,
    entity: `all${name}Meta`,
    _type: 'count',
  }

  ///////////////////////////////////////////////////
  //GET ALL (NO META)
  ///////////////////////////////////////////////////
  const allNoMetaQuery = `
    ${fragmentQuery}
    query all${name}($filter: JSON!, $options: JSON) {
      all${name}(filter: $filter, options: $options) {
      ${fields}
    }

    }
  `
  configs.allNoMetaConfig = {
    query: gql(allNoMetaQuery),
    queryAsString: allNoMetaQuery,
    entity: `all${name}`,
    _type: 'allNoMeta',
  }

  ///////////////////////////////////////////////////
  //SEARCH QUERY
  ///////////////////////////////////////////////////
  const searchQuery = `
    ${fragmentQuery}
    query search${name}($filter: JSON!, $options: JSON) {
      search${name}(filter: $filter, options: $options) {
      ${fields}
    }
    }
  `
  configs.searchConfig = {
    query: gql(searchQuery),
    queryAsString: searchQuery,
    entity: `search${name}`,
    _type: 'search',
  }

  ///////////////////////////////////////////////////
  //DELETE MANY
  ///////////////////////////////////////////////////
  const deleteManyQuery = `mutation deleteMany${name}($filter: JSON!) {
    deleteMany${name}(filter: $filter) {
    acknowledged
    deletedCount
  }
  }`
  configs.deleteManyConfig = {
    _type: 'deleteMany',
    entity: `deleteMany${name}`,
    query: gql(deleteManyQuery),
    queryAsString: deleteManyQuery,
    refetchQueries: [`all${name}`],
  }

  ///////////////////////////////////////////////////
  //UPDATE MANY
  ///////////////////////////////////////////////////
  const updateManyQuery = `mutation updateMany${name}($input : JSON!, $filter: JSON) {
    updateMany${name}(input: $input   filter: $filter) {
    ok
    n
    nModified
  }
  }`
  configs.updateManyConfig = {
    _type: 'updateMany',
    entity: `updateMany${name}`,
    query: gql(updateManyQuery),
    queryAsString: updateManyQuery,
    refetchQueries: [`all${name}`],
  }

  ///////////////////////////////////////////////////
  //SUB DOCUMENTS
  ///////////////////////////////////////////////////
  //loops for sub-documents
  subDocuments.forEach((m) => {
    const subDelete = `
      ${fragmentQuery}
      mutation delete${name}_${m}($filter: JSON!) {
        delete${name}_${m}(filter: $filter) {
          ${fields}
      }
      }`

    configs[`delete${name}_${m}Config`] = {
      _type: 'delete',
      entity: `delete${name}_${m}`,
      query: gql(subDelete),
      queryAsString: subDelete,

      refetchQueries: [`all${name}`],
    }

    const subUpdate = `
      ${fragmentQuery}
      mutation update${name}_${m}($input: JSON!, $filter: JSON!) {
        update${name}_${m}(input: $input,  filter: $filter) {
          ${fields}
        }
      }`

    configs[`update${name}_${m}Config`] = {
      _type: 'update',
      entity: `update${name}_${m}`,
      query: gql(subUpdate),
      queryAsString: subUpdate,

      refetchQueries: [`all${name}`, `single${name}`],
    }

    const subCreate = `
      ${fragmentQuery}
      mutation create${name}_${m}($input: JSON!, $filter: JSON!) {
        create${name}_${m}(input: $input,  filter: $filter) {
          ${fields}
        }
      }`

    configs[`create${name}_${m}Config`] = {
      _type: 'create',
      entity: `create${name}_${m}`,
      query: gql(subCreate),
      queryAsString: subCreate,
      refetchQueries: [`all${name}`],
    }
  })

  ///////////////////////////////////////////////////
  // SUBSCRIPTION
  ///////////////////////////////////////////////////
  const createdSubscriptionEntity = `created${name}`
  const createdSubscriptionQuery = `
  ${fragmentQuery}
  subscription ${createdSubscriptionEntity}($filter: JSON) {
    ${createdSubscriptionEntity}(filter: $filter) {
    ${fields}
  }
  } `

  const updatedSubscriptionEntity = `updated${name}`
  const updatedSubscriptionQuery = `
  ${fragmentQuery}
  subscription ${updatedSubscriptionEntity}($filter: JSON) {
    ${updatedSubscriptionEntity}(filter: $filter) {
    ${fields}
  }
  } `

  const deletedSubscriptionEntity = `deleted${name}`
  const deletedSubscriptionQuery = `
  ${fragmentQuery}
  subscription ${deletedSubscriptionEntity}($filter: JSON) {
    ${deletedSubscriptionEntity}(filter: $filter) {
    ${fields}
  }
  } `

  configs.subscription = {
    created: {
      _type: 'createdSubscription',
      queryAsString: createdSubscriptionQuery,
      query: gql(createdSubscriptionQuery),
      entity: createdSubscriptionEntity,
    },
    updated: {
      _type: 'updatedSubscription',
      queryAsString: updatedSubscriptionQuery,
      query: gql(updatedSubscriptionQuery),
      entity: updatedSubscriptionEntity,
    },
    deleted: {
      _type: 'deletedSubscription',
      queryAsString: deletedSubscriptionQuery,
      query: gql(deletedSubscriptionQuery),
      entity: deletedSubscriptionEntity,
    },
  }

  return configs
}

/**
 * Extracts fields from a GraphQL SelectionSet, handling fragments recursively
 * @param selectionSet - The GraphQL SelectionSet to extract fields from
 * @param fragmentMap - Map of fragment names to their definitions
 * @returns String representation of the fields
 */
export const extractFieldsFromSelectionSet = (selectionSet: SelectionSetNode, fragmentMap: Record<string, any>): string => {
  if (!selectionSet) return ''

  return selectionSet.selections
    .map((selection: any) => {
      if (selection.kind === Kind.FRAGMENT_SPREAD) {
        const fragmentName = selection.name.value
        const fragment = fragmentMap[fragmentName]

        return fragment ? extractFieldsFromSelectionSet(fragment.selectionSet, fragmentMap) : ''
      }
      if (selection.kind === Kind.FIELD) {
        const subFields = selection.selectionSet ? extractFieldsFromSelectionSet(selection.selectionSet, fragmentMap) : ''
        return subFields ? `${selection.name.value} { ${subFields} }` : selection.name.value
      }
      return ''
    })
    .filter(Boolean)
    .join('\n')
}

/**
 * Builds a subscription query from an existing list/single query
 * @param sourceQuery - The source GraphQL query (e.g., allOrder, singleOrder)
 * @param subscriptionType - Type of subscription: 'created', 'updated', or 'deleted'
 * @returns Object containing the subscription query and subscription name
 */
export const buildSubscriptionQueryFromQuery = (
  sourceQuery: DocumentNode,
  subscriptionType: 'created' | 'updated' | 'deleted'
): { subscriptionQuery: DocumentNode; subscriptionName: string } => {
  const operationName = getOperationName(sourceQuery)

  const fragmentMap: Record<string, any> = {}

  // Store fragments for later lookup
  sourceQuery.definitions.forEach((definition: DefinitionNode) => {
    if (definition.kind === Kind.FRAGMENT_DEFINITION) {
      fragmentMap[definition.name.value] = definition
    }
  })

  const operation = sourceQuery.definitions.find((def) => def.kind === Kind.OPERATION_DEFINITION)

  if (!operation || operation.kind !== Kind.OPERATION_DEFINITION) {
    throw new Error('Invalid GraphQL query: no operation definition found')
  }

  const entityField = operation.selectionSet.selections.find(
    (selection) => selection.kind === Kind.FIELD && selection.name.value === operationName
  ) as FieldNode | undefined

  if (!entityField) {
    throw new Error(`Could not build subscription query: cannot extract fields for entity: ${operationName}`)
  }

  const fields = extractFieldsFromSelectionSet(entityField.selectionSet, fragmentMap)

  // Extract entity name by removing common prefixes (single, all)
  const entityName = operationName.replace(/^single/, '').replace(/^all/, '')
  const subscriptionName = camelCase(`${subscriptionType} ${entityName}`) // e.g., "createdOrder", "updatedOrder", "deletedOrder"

  const queryString = `
    subscription ${subscriptionName}($filter: JSON) {
      ${subscriptionName}(filter: $filter) {
        ${fields}
      }
    }
  `

  return {
    subscriptionQuery: gql(queryString),
    subscriptionName,
  }
}
